import { useQuery } from '@tanstack/react-query';
import { DataUploadFileResponse, UploadedFile } from './types';
import { mapDetectedClients } from './mapDetectedClients';
import { useFileUploadActions } from './useFileUploadActions';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';

const PROCESSING_POLL_INTERVAL_MS = 5000;
const IN_FLIGHT_STATUSES = ['PENDING', 'PROCESSING'];

// A file whose background process has not finished has no detections written yet, so anything
// reading them sees an empty result rather than "not ready".
export const isFileProcessing = (file: UploadedFile | undefined): boolean =>
  IN_FLIGHT_STATUSES.includes(file?.backgroundProcess?.status ?? '');

const toUploadedFile = (file: DataUploadFileResponse): UploadedFile => ({
  ...file,
  detectedClients: mapDetectedClients(file.detectedClients),
});

interface UseFileUploadOptions {
  autoRefreshProcessing?: boolean;
  clientId?: string;
  familyId?: string;
  enabled?: boolean;
}

interface UseFileUploadResult {
  file: UploadedFile | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  deleteFile: () => Promise<void>;
  setFileResolved: (isResolved: boolean) => Promise<void>;
  moveFile: (parentId: string | null) => Promise<void>;
  getFileStatus: () => { color: string; label: string; showLoader?: boolean };
  refetchFile: () => Promise<void>;
}

export const useFileUpload = (
  uploadedFileId: UploadedFile['id'] | undefined,
  { autoRefreshProcessing = false, clientId, familyId, enabled = true }: UseFileUploadOptions = {},
): UseFileUploadResult => {
  const { clientId: defaultClientId } = useCurrentClientIfAvailable();
  const selectedClientId = clientId || defaultClientId;
  const actions = useFileUploadActions({ clientId: selectedClientId });

  const queryKey = familyId
    ? ['families', familyId, 'data-upload-files', uploadedFileId]
    : ['clients', selectedClientId, 'data-upload-files', uploadedFileId];

  const {
    data: file,
    isLoading,
    isError,
    refetch,
  } = useQuery<DataUploadFileResponse, Error, UploadedFile>({
    queryKey,
    enabled: !!uploadedFileId && enabled,
    select: toUploadedFile,
    // Poll only while the file is still being processed; stop once the background
    // process reaches a terminal state so a fully-processed file isn't polled forever.
    refetchInterval: autoRefreshProcessing
      ? query =>
          IN_FLIGHT_STATUSES.includes(query.state.data?.backgroundProcess?.status ?? '')
            ? PROCESSING_POLL_INTERVAL_MS
            : false
      : false,
  });

  const refetchFile = async () => {
    await refetch();
  };

  return {
    file,
    isLoading,
    error: isError ? new Error('Failed to load file') : actions.error,
    isError,
    deleteFile: () => {
      if (!uploadedFileId) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.deleteFile(uploadedFileId);
    },
    setFileResolved: (isResolved: boolean) =>
      file
        ? actions.setFileResolved(file, isResolved)
        : Promise.reject(new Error('File not found')),
    moveFile: (parentId: string | null) => {
      if (!file?.file) {
        return Promise.reject(new Error('File ID is required'));
      }
      return actions.moveFile(file, parentId);
    },
    getFileStatus: () =>
      file ? actions.getFileStatus(file) : { color: 'gray.6', label: 'Not Found' },
    refetchFile,
  };
};
